/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2016, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../../widgets/livewidget/nls/StringResources',
	'../../../../dataSources/metadata/MetadataColumn',
	'../../../../apiHelpers/SlotAPIHelper',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../api/SlotActionsProviderAPI',
	'../../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI'
], function(_, stringResources, MetadataColumn, SlotAPIHelper, APIFactory, SlotActionsProviderAPI, ContentActionsProviderAPI) {

	return class AggregationTypeAction {

		constructor({ features, content, dashboardAPI }) {
			this.content = content;
			this._slotActions = features.SlotActions;

			this._dashboard = features.API || dashboardAPI;
			this._icons = features.Icons || features['Dashboard.Icons'];
			this._dashboardState = features.DashboardState;
			this._contentActions = features.ContentActions;
		}

		initialize() {
			if(this._slotActions) {
				this._slotActions.registerProvider('aggregationTypeAction', this.getAPI());
			}

			if(this._contentActions) {
				this._contentActions.registerProvider('aggregationTypeAction', this.getAPI());
			}
		}

		_getSlotById(slotId, visualization) {
			return visualization.getSlots().getSlot(slotId);
		}

		_isMultiMeasuresSeriesSlot(slotApi, itemIndex) {
			return itemIndex === undefined ? SlotAPIHelper.isMultiMeasuresSeriesSlot(slotApi) || SlotAPIHelper.isMultiMeasuresValueSlot(slotApi) : false;
		}


		_supportAction(slotApi, dataItemApi, itemIndex, visualization) {
			const dataSource = visualization.getDataSource();
			if (!slotApi || !dataItemApi || this._isMultiMeasuresSeriesSlot(slotApi, itemIndex) || dataSource.getMetadataColumn(dataItemApi.getColumnId()) === null) {
				return false;
			}

			const aggregationType = dataItemApi.getMetadataColumn() && dataItemApi.getMetadataColumn().getAggregationMode();
			if (aggregationType === 'static' && dataItemApi.getType() === 'fact') {
				return false;
			}

			const dataSlotDefType = slotApi.getDefinition() && slotApi.getDefinition().getType();
			return (aggregationType !== 'static' &&
				(dataSlotDefType === 'ordinal' || dataSlotDefType === 'any')
			);
		}

		/**
		 * @function getContentActionList
		 * 
		 * @param {String[]} idList
		 * @return {Object[]} available content action list
		 */
		getContentActionList(idList) {
			if(idList.length === 1) {
				const state = this._dashboardState.getUiState();
				const contentId = idList[0];
				const content = this._dashboard.getCanvas().getContent(contentId);
				const visualization = content.getFeature('Visualization');
				if (visualization){
					const actions = visualization.getDefinition().getProperty('actions');
					let supportAction = _.find(actions, (action) => { return action.id === 'AggregationTypeAction'; });
					if (supportAction && state.eventGroups !== true) {
						return this.getContribution(visualization, supportAction.slot);
					}
				}
			}
			return [];
		}

		getContribution(visualization, dataSlotId, itemIndex) {
			let index = itemIndex;
			if (!dataSlotId || Array.isArray(itemIndex) && itemIndex.length > 1) {
				return [];
			}

			if (itemIndex === undefined || itemIndex === null) {
				itemIndex = 0;
			} else if (Array.isArray(itemIndex) && itemIndex.length === 1) {
				itemIndex = itemIndex[0];
			}

			const slotApi = this._getSlotById(dataSlotId, visualization);
			const dataItemList = slotApi.getDataItemList();
			const dataItemApi = dataItemList.length > itemIndex ? dataItemList[itemIndex] : null;
			if (!dataItemApi || !this._supportAction(slotApi, dataItemApi, index, visualization)) {
				return [];
			}

			const viewState = this.getViewState(dataSlotId, itemIndex, visualization);
			const aggregateIcon = this._icons.getIcon('dashboard-aggregate');

			return [{
				name: 'aggregationTypeAction',
				label: stringResources.get('toolbarActionAggregationType'),
				icon: aggregateIcon.id,
				type: 'NextView',
				view: {
					module: 'dashboard-analytics/lib/@waca/dashboard-common/dist/ui/dialogs/CommonSelectItemsDialog',
					state: viewState
				},
				actions: {
					apply: this.applyAggregation.bind(this, visualization, dataSlotId, itemIndex)
				}
			}];
		}

		getSlotActionList(dataSlotId, itemIndex) {
			const visualization = this.content.getFeature('Visualization');
			return this.getContribution(visualization, dataSlotId, itemIndex);
		}

		/**
		 * This method is called by the CommonSelectItemsDialog when an item is selected.
		 * @param dataSlotId
		 * @param itemIndex
		 * @param selectedItem - id of the aggregation type to apply against our columnId.
		 * 						Please see _getPossibleItems for more information
		 */
		applyAggregation(visualization, dataSlotId, itemIndex, selectedItem) {
			const slotApi = this._getSlotById(dataSlotId, visualization);
			let selectedAggregationType = (selectedItem === 'auto') ? undefined : selectedItem;
			const dataItemApi = slotApi.getDataItemList()[itemIndex];
			dataItemApi.setAggregation(selectedAggregationType);
		}

		/**
		 * This method creates an array of possible items to display in our Dialog.
		 * @param dataSlotId
		 * @param itemIndex
		 * @param columnType - Effective Column Type For Aggregation
		 * @returns an array of possible items, each possible item will be of the form
		 * 			{
		 * 				id: an id which is returned when a selection is made,
		 * 				localizedString: a string which is displayed in the dialog
		 * 			}
		 */
		_getPossibleItems(dataSlotId, itemIndex, columnType, visualization) {

			const slotApi = this._getSlotById(dataSlotId, visualization);
			const dataItemApi = slotApi.getDataItemList()[itemIndex];
			let _defaultAggregationType = null;
			if (slotApi && dataItemApi) {
				const metadataColumn = dataItemApi.getMetadataColumn();
				_defaultAggregationType = metadataColumn && metadataColumn.getDefaultAggregation();
			}
			// Determine which list of aggregation types we need to display
			let itemIds;
			if( columnType === 'attribute'){
				if (slotApi.getDefinition().getType() === 'any') {
					itemIds = MetadataColumn.SUPPORTED_AGGREGATION_FOR_ATTRIBUTE;
				} else {
					itemIds = _.without(MetadataColumn.SUPPORTED_AGGREGATION_FOR_ATTRIBUTE, MetadataColumn.AGGREGATION_TYPE_MAP.none);
				}
			} else {
				itemIds = MetadataColumn.SUPPORTED_AGGREGATION_FOR_FACT;
			}

			let possibleItems = [];
			// Create each possible item
			_.each(itemIds, function(itemId) {
				let possibleItem = {
					id: itemId,
					localizedString: stringResources.get(itemId)
				};
				possibleItems.push(possibleItem);
			});

			if (columnType === 'fact') {
				let possibleItem = {
					id: 'auto',
					localizedString: stringResources.get('defaultAggregation_auto', {
						'aggregationType': stringResources.get(_defaultAggregationType)
					})
				};
				possibleItems.push(possibleItem);
			}

			return possibleItems;
		}

		_getCurrentSelectedItem(slotApi, itemIndex) {
			const dataItemApi = slotApi.getDataItemList()[itemIndex];
			const previousAggregationType = dataItemApi.getAggregation();
			const columnType = this._getEffectiveColumnTypeForAggregation(slotApi, itemIndex);
			const bSelectAuto = (columnType === 'fact' && dataItemApi.hasDefaultAggregation());
			return (bSelectAuto) ? 'auto' : previousAggregationType;
		}


		/**
		 * Check to see if we have the same column in both the categories and ordinals, if so, treat as attribute regardless of type
		 * returns the column type (fact or attribute) for purposes of setting allowable aggregation types
		 *
		 *  @param dataSlotId - data slotid  of interest
		 *  @param itemIndex - dataItem index of interest
		 *  @return 'attribute' if column type is attribute, or non-unique fact, 'fact' if ordinal and column type is fact
		 */
		_getEffectiveColumnTypeForAggregation(slotApi, itemIndex) {
			const dataItemApi = slotApi.getDataItemList()[itemIndex];

			let targetItem = dataItemApi;
			const metadataColumn = targetItem.getMetadataColumn();
			return metadataColumn && metadataColumn.getType();
		}

		getViewState(dataSlotId, itemIndex, visualization) {
			const slotApi = this._getSlotById(dataSlotId, visualization);

			//userSelectedValues is used to show the currently selected aggregationType values (if any) as checked.
			//The operator determines how the selection is shown (eg a check or an x)
			if (!slotApi) {
				return null;
			}

			const columnType = this._getEffectiveColumnTypeForAggregation(slotApi, itemIndex);
			if (typeof columnType === 'undefined') {
				return null;
			}
			return {
				height: 270,
				width: 140, // those are hints only; for the placement of the toolbar.
				getCurrentSelectedItem: this._getCurrentSelectedItem.bind(this, slotApi, itemIndex),
				possibleItems: this._getPossibleItems(dataSlotId, itemIndex, columnType, visualization)
			};
		}


		getAPI() {
			if (!this._api) {
				if (this._slotActions) {
					this._api = APIFactory.createAPI(this, [SlotActionsProviderAPI]);
				}
				if (this._contentActions) {
					this._api = APIFactory.createAPI(this, [ContentActionsProviderAPI]);
				}
			}
			return this._api;
		}
	};
});
